import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
// import { SiMicrosoft } from "react-icons/si";
import Spinner from "../components/Spinner";
import { authApi } from "../api/authApi";
import { ApiError, getApiErrorMessage } from "../api/apiClient";
import strivreLogo from "../assets/images/strivre-logo.svg";
import { useMsal } from '@azure/msal-react';
import { microsoftLoginRequest } from '../auth/msalConfig';
import { InteractionStatus } from '@azure/msal-browser';
import { GOOGLE_CLIENT_ID } from '../api/apiConfig';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const microsoftExchangeStarted = useRef(false);
  const googleClientId = GOOGLE_CLIENT_ID;

  function handleGoogleLogin() {
    const redirectUri = `${window.location.origin}/login/google`;

    const nonce = crypto.randomUUID();

    sessionStorage.setItem('google_nonce', nonce);

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  function MicrosoftLogo() {
    return (
      <span className="grid h-5 w-5 grid-cols-2 gap-[2px]">
        <span className="bg-[#F25022]" />
        <span className="bg-[#7FBA00]" />
        <span className="bg-[#00A4EF]" />
        <span className="bg-[#FFB900]" />
      </span>
    );
  }

  async function handleSubmit(
    e: React.FormEvent
  ) {
    e.preventDefault();

    setIsLoading(true);
    setError("");

    try {
      const res = await authApi.login(
        email,
        password
      );

      login(
        res.token,
        res.user,
        res.subscription
      );

      navigate("/");
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.code === "PASSWORD_EXPIRED"
      ) {
        navigate("/password-expired", {
          replace: true,
          state: {
            email,
          },
        });

        return;
      }

      setError(
        getApiErrorMessage(
          err,
          "Invalid email or password. Please try again."
        )
      );
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    async function completeMicrosoftLogin() {
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      if (accounts.length === 0) {
        return;
      }

      if (microsoftExchangeStarted.current) {
        return;
      }

      microsoftExchangeStarted.current = true;

      setIsMicrosoftLoading(true);
      setError('');

      try {
        const account = accounts[0];
        instance.setActiveAccount(account);

        const tokenResult = await instance.acquireTokenSilent({...microsoftLoginRequest, account});

        if (!tokenResult.idToken) {
          throw new Error(
            'Microsoft did not return an ID token.'
          );
        }

        const res = await authApi.microsoftLogin(tokenResult.idToken);
        login(res.token, res.user, res.subscription);

        navigate('/', { replace: true });
      } catch (err) {
        console.error('Microsoft sign-in completion failed:', err);
        microsoftExchangeStarted.current = false;

        setError(getApiErrorMessage(err, 'Unable to sign in with Microsoft. Please try again.'));
      } finally {
        setIsMicrosoftLoading(false);
      }
    }

    completeMicrosoftLogin();
  }, [accounts, inProgress, instance, login, navigate]);

  async function handleMicrosoftLogin() {
    setError('');

    try {
      await instance.loginRedirect(
        microsoftLoginRequest
      );
    } catch (err) {
      console.error(
        'Microsoft sign-in failed:',
        err
      );

      setError(
        'Unable to start Microsoft sign-in. Please try again.'
      );
    }
  }

  return (
    <div className="w-full max-w-md p-8 card bg-white shadow-xl border border-strivre-lightnavy animate-fade-in">
      {/* Logo & Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-5">
          <img
            src={strivreLogo}
            alt="Strivre"
            className="h-20 w-auto max-w-[240px] object-contain"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="email">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="input-base w-full"
            disabled={isLoading || isMicrosoftLoading}
            required
          />
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-slate-700 mb-1"
            htmlFor="password"
          >
            Password
          </label>

          <input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input-base w-full"
            disabled={isLoading || isMicrosoftLoading}
            required
          />

          <div className="mt-2 flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-strivre-burgundy hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg animate-shake">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || isMicrosoftLoading}
          className="btn-primary w-full py-3 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="h-5 w-5" />
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </button>
      </form>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-strivre-lightnavy" />
        </div>

        <div className="relative flex justify-center">
          <span className="bg-white px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
            More sign-in options
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isLoading || isMicrosoftLoading}
          className="relative w-full flex items-center justify-center gap-3 rounded-xl border border-strivre-lightnavy bg-white px-4 py-3 text-slate-700 font-medium transition-all hover:border-red-300 hover:bg-red-50 disabled:opacity-60"
        >
          <FcGoogle size={20} />
          <span>Continue with Google</span>
        </button>

        <button
          type="button"
          onClick={handleMicrosoftLogin}
          disabled={isLoading || isMicrosoftLoading}
          className="relative w-full flex items-center justify-center gap-3 rounded-xl border border-strivre-lightnavy bg-white px-4 py-3 text-slate-700 font-medium transition-all hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60"
        >
          {isMicrosoftLoading ? (
            <>
              <Spinner size="h-5 w-5" />
              <span>Signing in with Microsoft...</span>
            </>
          ) : (
            <>
              <MicrosoftLogo />
              <span>Continue with Microsoft 365</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
